/**
 * Inventory / Outfits API functions.
 *
 * Mirrors the codex/events feature pattern: thin wrappers over apiFetch that
 * unwrap DRF responses and surface useful error messages.
 *
 * Equip/unequip and apply/undress flow through the websocket action
 * dispatcher (see Task 13) — REST stays read-only for inventory state.
 */

import { apiFetch } from '@/evennia_replacements/api';
import { fetchAllPages } from '@/lib/pagination';
import type { components } from '@/generated/api';
import type {
  BodyRegion,
  CreateOutfitPayload,
  CreateOutfitSlotPayload,
  EquippedItem,
  EquipmentLayer,
  InstallLabStationPayload,
  ItemInstance,
  Outfit,
  OutfitSlot,
  PaginatedResponse,
  RepairLabStationPayload,
  UpdateOutfitPayload,
  UseItemResult,
} from './types';

type ItemFacetRead = components['schemas']['ItemFacetRead'];
type QualityTier = components['schemas']['QualityTier'];
type FacetCraftResult = components['schemas']['FacetCraftResult'];
export type CraftingQuote = components['schemas']['CraftingQuote'];
export type CraftableTemplate = components['schemas']['CraftableTemplate'];
export type StationStatus = components['schemas']['StationStatus'];
export type LabStationDetails = components['schemas']['LabStationDetails'];
// install/upgrade only ever start a Project — they do NOT synchronously
// create a LabStationDetails row (level/is_broken/durability don't exist on
// this response). repair returns just the two durability fields, no
// level/is_broken. Both are now correctly modeled server-side via
// @extend_schema on LabStationViewSet (world/items/views_station.py), so
// these are the real generated shapes, not hand-typed workarounds.
export type RoomFeatureProjectStartResult = components['schemas']['RoomFeatureProjectStartResult'];
export type LabStationRepairResult = components['schemas']['LabStationRepairResult'];

const BASE_URL = '/api/items';

async function readError(res: Response, fallback: string): Promise<string> {
  try {
    const err = await res.json();
    return err.detail || err.non_field_errors?.[0] || fallback;
  } catch {
    return fallback;
  }
}

// ---------------------------------------------------------------------------
// Outfits
// ---------------------------------------------------------------------------

export async function listOutfits(characterSheetId: number): Promise<Outfit[]> {
  return fetchAllPages<Outfit>(
    `${BASE_URL}/outfits/?character_sheet=${characterSheetId}`,
    'Failed to load outfits'
  );
}

export async function getOutfit(id: number): Promise<Outfit> {
  const res = await apiFetch(`${BASE_URL}/outfits/${id}/`);
  if (!res.ok) {
    throw new Error(await readError(res, 'Failed to load outfit'));
  }
  return res.json();
}

export async function createOutfit(payload: CreateOutfitPayload): Promise<Outfit> {
  const res = await apiFetch(`${BASE_URL}/outfits/`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(await readError(res, 'Failed to create outfit'));
  }
  return res.json();
}

export async function updateOutfit(id: number, payload: UpdateOutfitPayload): Promise<Outfit> {
  const res = await apiFetch(`${BASE_URL}/outfits/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(await readError(res, 'Failed to update outfit'));
  }
  return res.json();
}

export async function deleteOutfit(id: number): Promise<void> {
  const res = await apiFetch(`${BASE_URL}/outfits/${id}/`, { method: 'DELETE' });
  if (res.status !== 204) {
    throw new Error(await readError(res, 'Failed to delete outfit'));
  }
}

// ---------------------------------------------------------------------------
// Outfit slots
// ---------------------------------------------------------------------------

export async function listOutfitSlots(outfitId: number): Promise<OutfitSlot[]> {
  const res = await apiFetch(`${BASE_URL}/outfit-slots/?outfit=${outfitId}`);
  if (!res.ok) {
    throw new Error(await readError(res, 'Failed to load outfit slots'));
  }
  const data = (await res.json()) as PaginatedResponse<OutfitSlot>;
  return data.results;
}

export async function createOutfitSlot(payload: CreateOutfitSlotPayload): Promise<OutfitSlot> {
  const res = await apiFetch(`${BASE_URL}/outfit-slots/`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(await readError(res, 'Failed to add outfit slot'));
  }
  return res.json();
}

export async function deleteOutfitSlot(id: number): Promise<void> {
  const res = await apiFetch(`${BASE_URL}/outfit-slots/${id}/`, { method: 'DELETE' });
  if (res.status !== 204) {
    throw new Error(await readError(res, 'Failed to remove outfit slot'));
  }
}

// ---------------------------------------------------------------------------
// Inventory + equipped (read-only)
// ---------------------------------------------------------------------------

export async function listInventory(characterId: number): Promise<ItemInstance[]> {
  // Follow every page (2026-07 audit): the endpoint paginates at 50 and this
  // read hydrates the wardrobe's instance lookup — a worn item past page 1
  // vanished from Currently Worn and made its Wear/Drop buttons no-op.
  return fetchAllPages<ItemInstance>(
    `${BASE_URL}/inventory/?character=${characterId}`,
    'Failed to load inventory'
  );
}

export async function postUseItem(itemId: number): Promise<UseItemResult> {
  const res = await apiFetch(`${BASE_URL}/inventory/${itemId}/use/`, { method: 'POST' });
  if (!res.ok) {
    throw new Error(await readError(res, 'Failed to use item'));
  }
  return (await res.json()) as UseItemResult;
}

export interface RemoveAccentResult {
  removed: string;
}

export interface RecycleResult {
  salvaged: [string, number][];
}

export async function postRemoveAccent(
  itemId: number,
  accentTarget: number
): Promise<RemoveAccentResult> {
  const res = await apiFetch(`${BASE_URL}/inventory/${itemId}/remove-accent/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ accent_target: accentTarget }),
  });
  if (!res.ok) {
    throw new Error(await readError(res, 'Failed to remove accent'));
  }
  return (await res.json()) as RemoveAccentResult;
}

export async function postRecycleItem(itemId: number): Promise<RecycleResult> {
  const res = await apiFetch(`${BASE_URL}/inventory/${itemId}/recycle/`, { method: 'POST' });
  if (!res.ok) {
    throw new Error(await readError(res, 'Failed to recycle item'));
  }
  return (await res.json()) as RecycleResult;
}

export async function postRequestRecycleApproval(itemId: number): Promise<{ status: string }> {
  const res = await apiFetch(`${BASE_URL}/inventory/${itemId}/request-recycle-approval/`, {
    method: 'POST',
  });
  if (!res.ok) {
    throw new Error(await readError(res, 'Failed to request GM sign-off'));
  }
  return (await res.json()) as { status: string };
}

export async function listEquipped(characterId: number): Promise<EquippedItem[]> {
  return fetchAllPages<EquippedItem>(
    `${BASE_URL}/equipped-items/?character=${characterId}`,
    'Failed to load equipped items'
  );
}

// ---------------------------------------------------------------------------
// Visible-worn (look-at-character) read-only endpoints
// ---------------------------------------------------------------------------

/**
 * Slim shape returned by ``GET /api/items/visible-worn/?character=N``.
 *
 * The list endpoint intentionally omits quality/template details — callers
 * follow up with ``getVisibleItemDetail`` to fetch the full ItemInstance
 * shape when the player drills into a specific item.
 */
export interface VisibleWornItem {
  id: number;
  display_name: string;
  body_region: BodyRegion;
  equipment_layer: EquipmentLayer;
  /** The viewer-visible active wearer persona used as a target assertion. */
  owner_persona_id?: number;
}

/** A visible body marking (#2985) — tattoo, scar, brand, birthmark, rune. */
export interface VisibleMarking {
  id: number;
  name: string;
  kind: string;
  body_region: BodyRegion;
  description: string;
}

/**
 * Fetch the visible-worn list for a target character, observed by the
 * caller's currently active character (``observerId``).
 *
 * The backend requires ``observer`` for non-staff: it identifies which
 * of the user's characters is doing the looking. Same-room and
 * self-look bypass rules are enforced server-side.
 */
export async function listVisibleWornItems(
  characterId: number,
  observerId: number
): Promise<VisibleWornItem[]> {
  const res = await apiFetch(
    `${BASE_URL}/visible-worn/?character=${characterId}&observer=${observerId}`
  );
  if (!res.ok) {
    throw new Error(await readError(res, 'Failed to load visible worn items'));
  }
  // The visible-worn list endpoint is computed from a service rather than a
  // queryset, so it returns a plain array (no DRF pagination wrapper).
  return (await res.json()) as VisibleWornItem[];
}

/**
 * Fetch the visible body markings for a target character (#2985) — the
 * sibling of the visible-worn list, same observer/permission contract.
 */
export async function listVisibleMarkings(
  characterId: number,
  observerId: number
): Promise<VisibleMarking[]> {
  const res = await apiFetch(
    `${BASE_URL}/visible-markings/?character=${characterId}&observer=${observerId}`
  );
  if (!res.ok) {
    throw new Error(await readError(res, 'Failed to load visible markings'));
  }
  // Computed from a service, plain array — no pagination wrapper.
  return (await res.json()) as VisibleMarking[];
}

/**
 * Fetch the full ItemInstance shape for a visibly worn item.
 *
 * The backend permission check requires ``observer`` for non-staff: the
 * observer must belong to the requesting account, and either match the
 * wearing character (self-look) or share a room with them.
 */
export async function getVisibleItemDetail(
  itemId: number,
  observerId: number
): Promise<ItemInstance> {
  const res = await apiFetch(`${BASE_URL}/visible-item-detail/${itemId}/?observer=${observerId}`);
  if (!res.ok) {
    throw new Error(await readError(res, 'Failed to load item details'));
  }
  return (await res.json()) as ItemInstance;
}

// ---------------------------------------------------------------------------
// Item facets (crafting)
// ---------------------------------------------------------------------------

export async function listItemFacets(itemInstanceId: number): Promise<ItemFacetRead[]> {
  const res = await apiFetch(`${BASE_URL}/item-facets/?item_instance=${itemInstanceId}`);
  if (!res.ok) throw new Error(await readError(res, 'Failed to load facets'));
  const data = (await res.json()) as PaginatedResponse<ItemFacetRead>;
  return data.results;
}

export async function craftAttachFacet(payload: {
  item_instance: number;
  facet: number;
}): Promise<FacetCraftResult> {
  const res = await apiFetch(`${BASE_URL}/item-facets/`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(await readError(res, 'Failed to attach facet'));
  return res.json();
}

export async function removeItemFacet(id: number): Promise<void> {
  const res = await apiFetch(`${BASE_URL}/item-facets/${id}/`, { method: 'DELETE' });
  if (!res.ok) throw new Error(await readError(res, 'Failed to remove facet'));
}

export async function getQualityTiers(): Promise<QualityTier[]> {
  const res = await apiFetch(`${BASE_URL}/quality-tiers/`);
  if (!res.ok) throw new Error(await readError(res, 'Failed to load quality tiers'));
  return res.json();
}

export async function getCraftingQuote(
  itemInstanceId: number,
  facetId: number
): Promise<CraftingQuote> {
  const res = await apiFetch(
    `${BASE_URL}/item-facets/quote/?item_instance=${itemInstanceId}&facet=${facetId}`
  );
  if (!res.ok) throw new Error(await readError(res, 'Failed to load crafting quote'));
  return res.json();
}

// ---------------------------------------------------------------------------
// Item creation — mint a new item from a recipe (#2211 core, #2240 web surface)
// ---------------------------------------------------------------------------

export interface CreateItemResult {
  created: boolean;
  item_instance_id: number | null;
  quality_tier: string | null;
  consequence_label: string | null;
}

/** The item-creation recipes this character can craft (knowledge gating is #2242). */
export async function listCraftableRecipes(): Promise<CraftableTemplate[]> {
  const res = await apiFetch(`${BASE_URL}/crafting/create/recipes/`);
  if (!res.ok) throw new Error(await readError(res, 'Failed to load craftable recipes'));
  return res.json();
}

/** A read-only cost/quality quote for minting one template. */
export async function getCreateItemQuote(templateId: number): Promise<CraftingQuote> {
  const res = await apiFetch(`${BASE_URL}/crafting/create/quote/?template=${templateId}`);
  if (!res.ok) throw new Error(await readError(res, 'Failed to load crafting quote'));
  return res.json();
}

/** Roll the check and (on success) mint a new item instance. */
export async function craftCreateItem(payload: {
  template: number;
  custom_name?: string;
  custom_description?: string;
}): Promise<CreateItemResult> {
  const res = await apiFetch(`${BASE_URL}/crafting/create/`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(await readError(res, 'Failed to craft item'));
  return res.json();
}

// ---------------------------------------------------------------------------
// Lab stations (#1234)
// ---------------------------------------------------------------------------

export async function getLabStationStatus(featureInstanceId: number): Promise<LabStationDetails> {
  const res = await apiFetch(`${BASE_URL}/lab-stations/${featureInstanceId}/`);
  if (!res.ok) throw new Error(await readError(res, 'Failed to load Lab station status'));
  return res.json();
}

export async function installLabStation(
  payload: InstallLabStationPayload
): Promise<RoomFeatureProjectStartResult> {
  const res = await apiFetch(`${BASE_URL}/lab-stations/install/`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(await readError(res, 'Failed to install Lab station'));
  return res.json();
}

export async function upgradeLabStation(
  payload: InstallLabStationPayload
): Promise<RoomFeatureProjectStartResult> {
  const res = await apiFetch(`${BASE_URL}/lab-stations/upgrade/`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(await readError(res, 'Failed to upgrade Lab station'));
  return res.json();
}

export async function repairLabStation(
  featureInstanceId: number,
  payload: RepairLabStationPayload
): Promise<LabStationRepairResult> {
  const res = await apiFetch(`${BASE_URL}/lab-stations/${featureInstanceId}/repair/`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(await readError(res, 'Failed to repair Lab station'));
  return res.json();
}
